import { useRef, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { translateByLocale } from "shared/localizedText";
import { LessonFormData, LocalizedText } from "../types";
import { readLocale, walkTranslatableFields } from "../utils";
import { ALL_LOCALES } from "../constants";

type TranslationJob = {
  /** Dot-path into the form, e.g. "parts.0.title" */
  path: string;
  locale: string;
  text: string;
};

/**
 * Collect every LocalizedText field where the source locale has content but
 * one or more target locales are empty.
 */
const collectJobs = (data: LessonFormData, sourceLocale: string): TranslationJob[] => {
  const jobs: TranslationJob[] = [];
  const targetLocales = ALL_LOCALES.filter(l => l !== sourceLocale);

  for (const { path, value } of walkTranslatableFields(data.parts)) {
    const src = readLocale(value, sourceLocale).trim();
    if (!src) continue;
    for (const locale of targetLocales) {
      if (!readLocale(value, locale).trim()) {
        jobs.push({ path, locale, text: src });
      }
    }
  }

  return jobs;
};

export const useAutoTranslateAll = (form: UseFormMethods<LessonFormData>, sourceLocale: string) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const progressRef = useRef(0);

  const translateAll = async () => {
    const data = form.getValues();
    const jobs = collectJobs(data, sourceLocale);
    if (jobs.length === 0) return;

    setIsTranslating(true);
    progressRef.current = 0;
    setProgress({ done: 0, total: jobs.length });

    const total = jobs.length;
    await translateByLocale(jobs, sourceLocale, {
      onTranslated: (job, translated) => {
        // Re-read current value to avoid overwriting concurrent edits
        const current = form.getValues(job.path as any) as LocalizedText | undefined;
        // Only write if still empty (honour the "no overwrite" rule)
        if (current && !readLocale(current, job.locale).trim()) {
          form.setValue(job.path as any, { ...current, [job.locale]: translated }, { shouldDirty: true });
        }
      },
      onSettled: () => {
        progressRef.current++;
        // Batch progress updates — only re-render every 5 translations or on the last one.
        if (progressRef.current % 5 === 0 || progressRef.current === total) {
          setProgress({ done: progressRef.current, total });
        }
      },
    });

    setProgress({ done: total, total });
    setIsTranslating(false);
  };

  return { translateAll, isTranslating, progress };
};
